import {
  Button,
  CircularProgress,
  Grid, Input, InputAdornment,
  List,
  ListItemButton,
  ListItemIcon, ListItemText,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {HugeiconsIcon} from "@hugeicons/react";
import {File01Icon, Folder01Icon, Refresh01Icon, Search01Icon, Upload01Icon} from "@hugeicons/core-free-icons";
import {grey} from "@mui/material/colors";
import {Context as AppContext} from "../../../context/AppContext";

export const SipPage = ({project}) => {
  const {updateProject} = useContext(AppContext);
  const [host, setHost] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState(false);
  const [downloadFileLoading, setDownloadFileLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [modifiedFiles, setModifiedFiles] = useState({});

  useEffect(() => {
    if (selectedItem) {
      downloadFile();
    } else {
      setFileContent("");
    }
  }, [selectedItem])

  useEffect(() => {
    if (project && project?.configuration?.snorlax_api) {
      setHost(project?.configuration?.snorlax_api);
      handleConnectHost(project?.configuration?.snorlax_api);
    }
  }, [project?.configuration?.snorlax_api])

  const downloadFile = async () => {
    try {
      if (selectedItem?.name) {
        setDownloadFileLoading(true);
        const response = await fetch(`${host}/file/download?path=/${selectedItem?.name}&name=${selectedItem?.name}&id=${new Date().getTime()}`);
        if (!response.ok) {
          toast.error('Network response was not ok');
        }
        const text = await response.text();

        setFileContent(text || "");
      }
    } catch (err) {
      toast.error(`Failed to download file '${selectedItem?.name}'`)
    } finally {
      setDownloadFileLoading(false);
    }
  }

  const handleConnectHost = async (host) => {
    try {
      setLoading(true);
      if (host) {
        const response = await fetch(`${host}/file/view-folder?path=/`);
        const files = await response.json();
        if (project?.configuration?.snorlax_api !== host) {
          const newProject = {...project, configuration: {...(project.configuration || {}), snorlax_api: host}};
          await updateProject({...newProject})
        }
        setFiles(files);
        setIsConnected(true);
        setSearch("");
        setFileContent("");
        setSelectedItem(null);
      }
    } catch (e) {
      toast.error("Failed to connect to server")
    } finally {
      setLoading(false);
    }
  }

  const onConnect = async (e) => {
    e?.preventDefault();
    await handleConnectHost(host);
  }

  const onSave = async () => {
    try {
      setUploadFile(true);
      for (const [fileName, content] of Object.entries(modifiedFiles)) {
        const file = new Blob([content], {type: 'text/plain'});

        var data = new FormData();
        data.append('filePath', "/");
        data.append('fileName', fileName);
        data.append('file', file, fileName);

        const response = await fetch(`${host}/file/upload`, {
          method: 'POST',
          body: data
        });

        if (!response.ok) {
          toast.error(`Failed to upload file: ${fileName}`);
        } else {
          toast.success(`File ${fileName} uploaded successfully`);
        }
      }
      setModifiedFiles({});
    } catch (e) {
      toast.error("Failed to upload files");
    } finally {
      setUploadFile(false);
    }
  }

  const onDisconnect = (e) => {
    e?.preventDefault();
    setFiles([]);
    setIsConnected(false);
    setSearch("");
    setFileContent("");
    setSelectedItem(null);
    setModifiedFiles({});
  }

  const onDeploy = async () => {
    try {
      setDownloadFileLoading(true);
      await onSave();
      const response = await fetch(`${host}/asterisk/dialplan/reboot?id=${new Date().getTime()}`, {method: "POST"});
      if (!response.ok) {
        toast.error('Network response was not ok');
      }
      toast.success("Deploy was successful");
    } catch (err) {
      toast.error(`Failed to deploy`);
    } finally {
      setDownloadFileLoading(false);
    }
  }

  const handleFileChange = (content) => {
    setFileContent(content);
    setModifiedFiles(prev => ({...prev, [selectedItem.name]: content}));
  }

  const handleUpdateText = (e) => {
    const updatedText = e.target.innerText?.trim();
    if (fileContent !== updatedText) {
      handleFileChange(updatedText)
    }
  }


  return (
    <Grid container
          sx={{mx: 'auto', maxHeight: "calc(100dvh - 64px)", height: "100%", py: 3, px: 3}}>
      <Grid item xs={12} sx={{p: 1}}>
        <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"}>
          <Typography variant={"h4"} fontWeight={600}>Voice Connector Configuration</Typography>
          <Stack direction={"row"} gap={2}>
            <Button
              color={"secondary"} variant={"ghost"}
              startIcon={<HugeiconsIcon icon={Refresh01Icon} size={18}/>} className={"border-shadow"}
              sx={{bgcolor: grey[100], px: 3, borderRadius: "8px !important", "&:hover": {bgcolor: grey[300],}}}
              disabled={uploadFile || downloadFileLoading || loading}
              onClick={() => {
                setSelectedItem(null);
                setFileContent("");
              }}
            >
              Reset
            </Button>
            <Button
              color={"secondary"} variant={"modal"}
              startIcon={<HugeiconsIcon icon={Upload01Icon} size={18}/>}
              sx={{borderRadius: "8px !important", px: 3}}
              disabled={uploadFile || downloadFileLoading || loading} onClick={onDeploy}
            >
              Deploy
            </Button>
          </Stack>
        </Stack>
      </Grid>

      <Grid item xs={12} sm={4} lg={3} sx={{p: 1, height: 'calc(100% - 16px)'}}>
        <Stack direction={"column"} gap={2} className={"border-shadow"}
               sx={{
                 gap: 1, p: 2,
                 maxHeight: "calc(100dvh - 190px)",
                 height: "100%",
                 overflow: "hidden",
                 borderRadius: 2
               }}
        >
          <TextField
            label={"Host"} size={"small"}
            placeholder={"https://x.x.x.x:8000"}
            disabled={isConnected}
            value={host} onChange={e => setHost(e.target.value)}
            InputProps={{
              readOnly: isConnected,
              sx: {pr: 0.2, py: 0.2},
              endAdornment: <InputAdornment position={"end"} sx={{ml: 1, mr: 0, p: 0}}>
                {
                  isConnected ?
                    <Button disabled={!host || uploadFile || downloadFileLoading || loading} type="button"
                            onClick={onDisconnect} color={"error"} sx={{boxShadow: 0}}>
                      Disconnect
                    </Button>
                    :
                    <Button disabled={!host || uploadFile || downloadFileLoading || loading} type="submit"
                            color={"info"} sx={{boxShadow: 0}}>
                      Connect
                    </Button>
                }

              </InputAdornment>
            }}
          />
          <Typography fontWeight={600} fontSize={16}>Asterisk Configuration Files:</Typography>
          <Input
            startAdornment={<HugeiconsIcon icon={Search01Icon} size={16} style={{paddingRight: 8}}/>}
            value={search} placeholder={"Search files"}
            onChange={e => setSearch(e.target.value)}
          />
          {loading && <CircularProgress size={18}/>}
          <List sx={{overflowY: "auto", height: "100%", flex: 1}}>
            {files?.filter(item => item?.name?.toLowerCase().includes(search?.toLowerCase()))?.map(item => {
              const {name, isDir} = item;
              return (
                <ListItemButton
                  key={name}
                  onClick={() => setSelectedItem(item)}
                  sx={{
                    p: 0.5,
                    bgcolor: name === selectedItem?.name ? "rgba(0,0,0,0.05)" : "transparent",
                    borderRadius: "4px !important"
                  }}
                >
                  <ListItemIcon>
                    {isDir ? <HugeiconsIcon icon={Folder01Icon}/> : <HugeiconsIcon icon={File01Icon}/>}
                  </ListItemIcon>
                  <ListItemText primary={name}/>
                </ListItemButton>
              )
            })}
          </List>
        </Stack>

      </Grid>

      <Grid item xs={12} sm={8} lg={9} sx={{p: 1, height: 'calc(100% - 16px)'}}>
        <Stack direction={"column"} gap={2}
               className={"border-shadow"}
               sx={{
                 gap: 1, p: 2,
                 maxHeight: "calc(100dvh - 190px)",
                 height: "100%",
                 overflow: "hidden",
                 borderRadius: 2
               }}
        >
          <Typography fontSize={17} fontWeight={600}>{selectedItem?.name || "Please Select File"}</Typography>
          {
            downloadFileLoading ?
              <CircularProgress size={18}/> :
              <Stack direction={'column'} sx={{flex: 1, overflowY: "auto", borderRadius: 1, bgcolor: fileContent ? "#c4c4c421" : "",}}>
                <Typography
                  fontSize={13}
                  component={"pre"}
                  suppressContentEditableWarning
                  contentEditable
                  sx={{
                    fontFamily: "Consolas",
                    p: 1,
                    outline: "none",
                    border: "none",
                    "&:focus": {
                      outline: "none",
                      border: "none"
                    }
                  }}
                  // onFocus={(e) => e.target.blur()} // 👈 Immediately removes focus
                  onBlur={handleUpdateText}
                >
                  {fileContent}
                </Typography>

              </Stack>
          }

        </Stack>
      </Grid>
    </Grid>
  )
}
